import type { OnboardingStepId } from '~/composables/use-onboarding';
import { storeToRefs } from 'pinia';
import { defineNuxtRouteMiddleware } from '#app/composables/router';
import { useAuth, useOnboardingStore } from '#layers/access/public';
import { useSystemInfo } from '#layers/base/public';
import { useWorkspaceStore } from '#layers/workspace/public';
import { readOnboardingProgress } from '~/composables/use-onboarding';
import { isUnderTest } from '~/utils/env/is-under-test';
import { isGuestOrPublicRoute } from '~/utils/routes';
import { useWorkspaceSwitch } from '~/workspace/use-workspace-switch';

const STEP_ROUTES: Record<OnboardingStepId, string> = {
	'workspace': 'onboarding.workspace',
	'data-source': 'onboarding.data-source',
	'connect': 'onboarding.connect',
};

// Step routes that can't function without a workspace (the workspace step is where one is made).
const WORKSPACE_STEP_ROUTES = new Set<string>([STEP_ROUTES['data-source'], STEP_ROUTES.connect]);

/**
 * Post-auth onboarding routing. For authenticated users past `2.private.global`:
 * Progress lives in `sessionStorage`, so resume is in-session only.
 */
export default defineNuxtRouteMiddleware(async (to) => {
	if (isUnderTest()) return;
	if (isGuestOrPublicRoute(to)) return;

	const onboarding = useOnboardingStore();
	const systemInfo = useSystemInfo();
	const { currentWorkspaceSlug } = storeToRefs(useWorkspaceStore());
	const { switchTo } = useWorkspaceSwitch();
	const auth = useAuth();
	await systemInfo.ensureLoaded();

	// `license-locked.global` owns the lock redirect; Must sit after the await above
	if (systemInfo.license.status === 'locked') return;

	if (!auth.loggedIn) return;
	// Existing session progress must not revive the workspace wizard in local development.
	if (import.meta.dev && import.meta.env.VITE_FORCE_ONBOARDING !== '1') return;

	if (onboarding.requireAcceptTerms) {
		if (to.name === 'onboarding.user') return;

		return { name: 'onboarding.user' };
	}

	if (onboarding.requireCreateUser) return;

	const userId = auth.currentUser?.id;
	if (!userId) return;

	const progress = readOnboardingProgress(userId);

	// No active onboarding: only guard a direct navigation to a step that can't
	// function without a workspace by sending it back to the workspace step.
	if (progress?.status !== 'active') {
		if (typeof to.name === 'string' && WORKSPACE_STEP_ROUTES.has(to.name)) {
			return { name: STEP_ROUTES.workspace };
		}

		return;
	}

	// Onboarding is active: pin the user to their step from anywhere in the app.
	let stepRoute = (progress.currentStep !== 'workspace' && !progress.workspaceSlug)
		? STEP_ROUTES.workspace
		: STEP_ROUTES[progress.currentStep];

	// Re-hydrate the workspace (and its permissions) from the session-storage slug
	if (progress.workspaceSlug && currentWorkspaceSlug.value !== progress.workspaceSlug) {
		try {
			await switchTo(progress.workspaceSlug);
		}
		catch {
			stepRoute = STEP_ROUTES.workspace;
		}
	}

	if (to.name === stepRoute) return;

	return { name: stepRoute };
});
